import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { HourglassMedium, ClockCountdown, XCircle, Prohibit, ArrowClockwise, SignOut } from "@phosphor-icons/react";

const STATES = {
  PENDING_REVIEW: {
    icon: HourglassMedium,
    title: "Accès en cours de validation",
    body: "Merci pour votre inscription. Notre équipe vérifie votre compte afin de garantir la qualité de la plateforme. Vous recevrez un email dès que votre accès sera activé.",
    tone: "accent",
  },
  WAITLISTED: {
    icon: ClockCountdown,
    title: "Vous êtes sur liste d'attente",
    body: "Votre demande a bien été reçue et placée sur liste d'attente. Nous ouvrons les accès progressivement — vous serez notifié par email dès qu'une place se libère pour votre ESN.",
    tone: "accent",
  },
  REJECTED: {
    icon: XCircle,
    title: "Demande non retenue",
    body: "Après examen, nous ne pouvons pas ouvrir d'accès à interKO pour le moment. Si vous pensez qu'il s'agit d'une erreur, contactez-nous à contact@interko.io.",
    tone: "danger",
  },
  SUSPENDED: {
    icon: Prohibit,
    title: "Accès suspendu",
    body: "L'accès de votre organisation à interKO a été suspendu. Contactez-nous à contact@interko.io pour rétablir votre compte.",
    tone: "danger",
  },
};

export default function PendingReview() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const status = user?.organization?.approval_status || "PENDING_REVIEW";
  const cfg = STATES[status] || STATES.PENDING_REVIEW;
  const Icon = cfg.icon;

  const recheck = async () => {
    await refresh();
    toast.info("Statut actualisé.");
  };

  return (
    <div className="min-h-screen bg-bg relative z-10 flex items-center justify-center p-6" data-testid="pending-review">
      <div className="w-full max-w-lg text-center">
        <div className="mb-8"><Logo size="text-2xl" /></div>
        <div className="surface rounded-2xl p-10">
          <div className={`w-16 h-16 rounded-xl mx-auto flex items-center justify-center mb-6 ${cfg.tone === "danger" ? "bg-destructive/12 border border-destructive/30" : "bg-accent/12 border border-accent/25"}`}>
            <Icon size={34} weight="duotone" className={cfg.tone === "danger" ? "text-destructive" : "text-accent"} />
          </div>
          <h1 className="font-display text-3xl font-bold mb-3" data-testid="pending-title">{cfg.title}</h1>
          <p className="text-muted text-sm leading-relaxed mb-8">{cfg.body}</p>

          <div className="bg-surface-2 border border-border rounded-lg px-4 py-3 mb-8 text-left">
            <div className="overline mb-1">Organisation</div>
            <div className="text-fg font-medium">{user?.organization?.name || user?.company || "—"}</div>
            <div className="text-xs text-muted font-mono mt-0.5">{user?.email}</div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={recheck} data-testid="pending-recheck-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-accent text-black font-semibold px-5 py-3 rounded-md hover-lift">
              <ArrowClockwise size={16} weight="bold" /> Vérifier mon statut
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="pending-logout-btn"
              className="flex items-center justify-center gap-2 border border-border text-muted hover:text-fg px-5 py-3 rounded-md hover-lift">
              <SignOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
        <p className="text-xs text-muted mt-6">Une question ? <a href="mailto:contact@interko.io" className="text-accent hover:underline">contact@interko.io</a></p>
      </div>
    </div>
  );
}
